export interface PaymentComplementPayloadOptions {
  bill: any;
  invoice: any;
  profile: any;
  paymentDate: unknown;
  paymentAmount?: unknown;
  paymentMethod?: unknown;
  parsedInvoice?: any;
  receptor?: any;
  relatedInvoices?: any[];
}

const pad = (value: number): string => value.toString().padStart(2, '0');

const finiteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const money = (value: number): string => value.toFixed(2);

export function toLocalDateInput(value: unknown = new Date()): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = value instanceof Date ? value : new Date(value as any);
  if (Number.isNaN(date.getTime())) {
    throw new Error('La fecha de pago no es válida.');
  }

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function toLocalDateTime(date = new Date()): string {
  return `${toLocalDateInput(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function resolveCfdiPaymentMethod(value: unknown): string {
  const method = String(value ?? '').trim().toUpperCase();
  const methods: Record<string, string> = {
    CASH: '01',
    EFECTIVO: '01',
    CHECK: '02',
    CHEQUE: '02',
    STP: '03',
    SPEI: '03',
    TRANSFER: '03',
    TRANSFERENCIA: '03',
    CARD: '04',
    CREDIT_CARD: '04',
    DEBIT_CARD: '28',
    VI: '04',
    VISA: '04',
    MC: '04',
    MASTERCARD: '04',
    AMEX: '04',
  };

  return methods[method] ?? (/^\d{2}$/.test(method) ? method : '99');
}

function invoiceRoot(parsedInvoice: any): any {
  return parsedInvoice?.['cfdi:Comprobante'];
}

function invoiceTransfer(parsedInvoice: any): any {
  return invoiceRoot(parsedInvoice)
    ?.['cfdi:Impuestos']?.[0]
    ?.['cfdi:Traslados']?.[0]
    ?.['cfdi:Traslado']?.[0]
    ?.['$'];
}

function normalizeTaxCode(value: unknown): string {
  const code = String(value ?? '002').replace(/^0+/, '');
  return code || '0';
}

function normalizeTaxFactor(value: unknown): string {
  const factor = String(value ?? 'Tasa').toUpperCase();
  if (/^\d+$/.test(factor)) {
    return factor;
  }
  return factor === 'CUOTA' ? '2' : factor === 'EXENTO' ? '3' : '1';
}

export function buildPaymentComplementPayload(options: PaymentComplementPayloadOptions): any {
  const {
    bill,
    invoice,
    profile,
    parsedInvoice,
    relatedInvoices = [],
  } = options;
  const root = invoiceRoot(parsedInvoice);
  const rootAttributes = root?.['$'] ?? {};
  const receptor = options.receptor ?? root?.['cfdi:Receptor']?.[0]?.['$'];
  const lugarExpedicion = rootAttributes['LugarExpedicion']
    ?? profile?.zip_code
    ?? profile?.postal_code;

  if (!invoice?.uuid || !invoice?.id) {
    throw new Error('La factura relacionada no tiene UUID o identificador.');
  }
  if (!receptor?.['Rfc'] || !receptor?.['Nombre']) {
    throw new Error('No fue posible obtener los datos fiscales del receptor.');
  }
  if (!lugarExpedicion) {
    throw new Error('No fue posible obtener el código postal de expedición.');
  }

  const previousBalance = finiteNumber(bill?.total);
  const requestedAmount = finiteNumber(options.paymentAmount, previousBalance);
  const paidAmount = requestedAmount > 0 ? requestedAmount : previousBalance;
  if (previousBalance <= 0 || paidAmount <= 0) {
    throw new Error('El monto del recibo o del pago no es válido.');
  }
  if (paidAmount - previousBalance > 0.005) {
    throw new Error('El monto pagado no puede ser mayor al saldo anterior.');
  }

  const remainingBalance = Math.max(previousBalance - paidAmount, 0);
  const ratio = Math.min(paidAmount / previousBalance, 1);
  const taxBase = finiteNumber(bill?.sub_total) * ratio;
  const taxAmount = finiteNumber(bill?.taxes) * ratio;
  const transfer = invoiceTransfer(parsedInvoice);
  const taxFactor = String(transfer?.['TipoFactor'] ?? 'Tasa').toUpperCase();
  const hasTaxData = taxBase > 0 && (Boolean(transfer) || taxAmount > 0);
  const hasTransferredTax = hasTaxData && taxFactor !== 'EXENTO';
  const hasExemptTax = hasTaxData && taxFactor === 'EXENTO';
  const taxRate = finiteNumber(transfer?.['TasaOCuota'], taxAmount / taxBase);
  const activeComplements = relatedInvoices.filter(
    related => Number(related?.tipo) === 6 && !related?.canceled
  ).length;

  const relatedDocument: any = {
    IdDocumento: invoice.uuid,
    Moneda: 'MXN',
    NumeroParcialidad: activeComplements + 1,
    ObjetoDeImpuesto: hasTaxData ? '02' : '01',
    ImporteSaldoAnterior: money(previousBalance),
    ImportePagado: money(paidAmount),
    ImporteSaldoInsoluto: money(remainingBalance),
  };
  const payment: any = {
    FechaPago: `${toLocalDateInput(options.paymentDate)}T00:00:00`,
    FormaPago: resolveCfdiPaymentMethod(options.paymentMethod),
    Moneda: 'MXN',
    DocumentosRelacionados: [relatedDocument],
  };
  const pagosV20: any = {
    Pagos: [payment],
    Totales: {
      MontoTotalPagos: money(paidAmount),
    },
  };

  if (hasTransferredTax) {
    const tax = {
      Impuesto: normalizeTaxCode(transfer?.['Impuesto']),
      Factor: normalizeTaxFactor(transfer?.['TipoFactor']),
      Tasa: taxRate.toFixed(6),
      Base: money(taxBase),
      Importe: money(taxAmount),
    };
    relatedDocument.Impuestos = { Trasladados: [tax] };
    payment.Impuestos = { Trasladados: [tax] };
    const ivaSuffix = Math.abs(taxRate - 0.16) < 0.000001
      ? 'IVA16'
      : Math.abs(taxRate - 0.08) < 0.000001
        ? 'IVA8'
        : 'IVA0';
    pagosV20.Totales[`TotalTrasladosBase${ivaSuffix}`] = money(taxBase);
    pagosV20.Totales[`TotalTrasladosImpuesto${ivaSuffix}`] = money(taxAmount);
  } else if (hasExemptTax) {
    const exemptTax = {
      Impuesto: normalizeTaxCode(transfer?.['Impuesto']),
      Factor: normalizeTaxFactor(transfer?.['TipoFactor']),
      Base: money(taxBase),
    };
    relatedDocument.Impuestos = { Trasladados: [exemptTax] };
    payment.Impuestos = { Trasladados: [exemptTax] };
    pagosV20.Totales.TotalTrasladosBaseIVAExento = money(taxBase);
  }

  return {
    Encabezado: {
      Fecha: toLocalDateTime(),
      Serie: invoice.serie,
      Folio: invoice.folio,
      MetodoPago: '',
      FormaPago: '',
      Moneda: '',
      LugarExpedicion: String(lugarExpedicion),
      SubTotal: '0',
      Total: '0',
      Receptor: {
        RFC: receptor['Rfc'],
        NombreRazonSocial: receptor['Nombre'],
        RegimenFiscal: receptor['RegimenFiscalReceptor'],
        UsoCFDI: '',
        Direccion: {
          Calle: '',
          NumeroExterior: '',
          NumeroInterior: '',
          Colonia: '',
          Localidad: '',
          Municipio: '',
          Estado: '',
          Pais: 'Mexico',
          CodigoPostal: receptor['DomicilioFiscalReceptor'],
        },
      },
    },
    Complemento: {
      PagosV20: pagosV20,
      TipoComplemento: 6,
    },
    complemento: {
      serie: 'C',
      folio: bill.id,
      related_invoice: invoice.id,
    },
    profile_id: profile?.id,
  };
}
